<template>
  <v-data-table
    :headers="headers"
    :items="formattedItems"
    :search="search"
    :loading="loading"
    :items-per-page="10"
    class="elevation-0 social-upload-table"
    dense
  >
    <template #item.platform="{ item }">
      <div class="d-flex align-center">
        <v-img
          :src="platformIcon(item.platformKey)"
          class="mr-2"
          width="24"
          height="24"
          contain
        />
        <span>{{ item.platform }}</span>
      </div>
    </template>

    <template #item.status="{ item }">
      <v-chip
        :color="statusColor(item.status)"
        text-color="white"
        small
        label
      >
        {{ statusLabel(item.status) }}
      </v-chip>
    </template>

    <template #item.privacy="{ item }">
      <v-chip small outlined color="primary" v-if="item.privacy">
        {{ item.privacy }}
      </v-chip>
      <span v-else>-</span>
    </template>

    <template #item.error_message="{ item }">
      <span class="one-line" :title="item.error_message || ''">
        {{ item.error_message || '-' }}
      </span>
    </template>

    <template #item.channel="{ item }">
      <div class="channel-cell">
        <div class="channel-line">
          <span class="channel-label">
            {{ $store.getters.t('social.history.channel_url_label') }}:
          </span>
          <template v-if="item.channelUrl">
            <a
              :href="formatLinkHref(item.channelUrl)"
              class="channel-link"
              target="_blank"
              rel="noopener"
            >
              {{ formatLinkText(item.channelUrl) }}
            </a>
          </template>
          <span v-else>-</span>
        </div>
        <div
          class="channel-line"
          v-if="shouldShowVideoLink(item)"
        >
          <span class="channel-label">
            {{ item.videoLabel }}:
          </span>
          <a
            :href="formatLinkHref(item.videoUrl)"
            class="channel-link"
            target="_blank"
            rel="noopener"
          >
            {{ formatLinkText(item.videoUrl) }}
          </a>
        </div>
      </div>
    </template>

    <template #no-data>
      <div class="py-6 text-subtitle-1 text-center grey--text text--darken-1">
        {{ $store.getters.t ? $store.getters.t('social.history.empty') : '소셜 업로드 이력이 없습니다.' }}
      </div>
    </template>
  </v-data-table>
</template>

<script>
export default {
  name: 'SocialUploadTable',
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    search: { type: String, default: '' },
  },
  data() {
    return {
      headers: [
        { text: this.$store.getters.t('social.upload.summary.platform'), value: 'platform', sortable: false },
        { text: this.$store.getters.t('social.upload.channel_label'), value: 'channel', sortable: false },
        { text: this.$store.getters.t('social.upload.title_label'), value: 'title', sortable: false },
        { text: this.$store.getters.t('common.status.label'), value: 'status', sortable: false },
        { text: this.$store.getters.t('social.upload.visibility.scope'), value: 'privacy', sortable: false },
        { text: this.$store.getters.t('common.date.created_at'), value: 'created_at', sortable: true },
        { text: this.$store.getters.t('common.date.completed_at'), value: 'finished_at', sortable: true },
        { text: this.$store.getters.t('common.error.message'), value: 'error_message', sortable: false },
      ],
      platformIcons: {
        youtube: require('@/styles/youtube.png'),
        instagram: require('@/styles/instgram.png'),
        default: require('@/styles/socialimage.png'),
      },
    };
  },
  computed: {
    formattedItems() {
      return (this.items || []).map(task => {
        const integration = task.integration || {};
        const platform = integration.platform || {};
        const payload = task.payload || {};

        return {
          id: task.id,
          platform: platform.name || platform.key || '-',
          platformKey: (platform.key || platform.name || 'default').toLowerCase(),
          channel: integration.external_name || integration.channel_url || integration.external_id || '-',
          channelUrl: this.resolveChannelUrl(integration),
          ...this.resolveVideoInfo(task, platform.key),
          title: payload.title || '-',
          status: task.status || '-',
          privacy: this.formatPrivacy(payload.privacy),
          created_at: this.formatDate(task.created_at),
          finished_at: this.formatDate(task.finished_at),
          error_message: task.error_message,
        };
      });
    },
  },
  methods: {
    formatPrivacy(value) {
      if (!value) return null;
      const map = {
        public: this.$store.getters.t('social.upload.visibility.public'),
        private: this.$store.getters.t('social.upload.visibility.private'),
        unlisted: this.$store.getters.t('social.upload.visibility.unlisted'),
      };
      const key = value.toLowerCase();
      return map[key] || value;
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    },
    statusColor(status) {
      switch ((status || '').toLowerCase()) {
        case 'completed':
          return 'green darken-1';
        case 'processing':
          return 'primary';
        case 'queued':
          return 'blue-grey';
        case 'failed':
          return 'red';
        case 'cancelled':
          return 'grey darken-1';
        default:
          return 'grey';
      }
    },
    statusLabel(status) {
      const key = (status || '').toLowerCase();
      switch (key) {
        case 'completed':
          return this.$store.getters.t('social.history.status.completed');
        case 'processing':
          return this.$store.getters.t('social.history.status.processing');
        case 'queued':
          return this.$store.getters.t('social.history.status.queued');
        case 'failed':
          return this.$store.getters.t('social.history.status.failed');
        case 'cancelled':
          return this.$store.getters.t('social.history.status.cancelled');
        default:
          if (!status) {
            return this.$store.getters.t('social.history.status.unknown');
          }
          return status;
      }
    },
    platformIcon(key) {
      return this.platformIcons[key] || this.platformIcons.default;
    },
    resolveChannelUrl(integration = {}) {
      const settings = (integration.settings && typeof integration.settings === 'object') ? integration.settings : {};
      const credentials = (integration.credentials && typeof integration.credentials === 'object') ? integration.credentials : {};
      return integration.channel_url || settings.channel_url || credentials.channel_url || '';
    },
    resolveVideoInfo(task, platformKey) {
      const key = (platformKey || '').toLowerCase();
      if (key !== 'youtube') {
        return { videoUrl: '', videoLabel: '' };
      }

      const remoteId = task?.remote_identifier || task?.response?.video_id || task?.response?.broadcast_id;
      if (!remoteId) {
        return { videoUrl: '', videoLabel: '' };
      }
      const videoUrl = this.buildYoutubeWatchUrl(remoteId);
      const labelKey = this.isLiveTask(task)
        ? 'social.history.live_url_label'
        : 'social.history.video_url_label';
      const videoLabel = this.$store.getters.t(labelKey);
      return { videoUrl, videoLabel };
    },
    buildYoutubeWatchUrl(videoId) {
      if (!videoId) return '';
      return `https://www.youtube.com/watch?v=${videoId}`;
    },
    formatLinkHref(url) {
      if (!url || typeof url !== 'string') return '#';
      const trimmed = url.trim();
      if (!trimmed) return '#';
      return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    },
    formatLinkText(text) {
      if (!text || typeof text !== 'string') return '';
      const trimmed = text.trim();
      return trimmed.length > 50 ? `${trimmed.slice(0, 50)}…` : trimmed;
    },
    shouldShowVideoLink(item) {
      return item?.platformKey === 'youtube' && !!item?.videoUrl;
    },
    isLiveTask(task) {
      return (task?.type || '').toLowerCase() === 'live_sync';
    },
  },
};
</script>

<style scoped>
.channel-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.channel-line {
  font-size: 13px;
  color: #4b5563;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.channel-label {
  font-weight: 600;
  color: #111827;
}

.channel-link {
  color: #1d4ed8;
  text-decoration: underline;
  word-break: break-all;
}

.social-upload-table ::v-deep .v-data-table-header {
  background: #fafbff;
}

.one-line {
  display: inline-block;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
